import React, { useState } from "react";
import { Box, Typography, Modal } from "@mui/material";
import { useTranslation } from "../../../hooks/useTranslation";
import SafeImage from "./SafeImage";

export const ImagePreviewBox = ({
  images,
  asins,
}: {
  images: string[];
  asins?: string[];
}) => {
  const { translateWithoutPrefix } = useTranslation();
  const [open, setOpen] = useState(false);

  if (!images || images.length === 0) {
    return null;
  }

  const getAmazonUrl = (i: number) => {
    const asin = asins?.[i];
    return asin ? `https://www.amazon.com/dp/${asin}` : undefined;
  };

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        height="100%"
        width="100%"
        gap={1}
        flexWrap="wrap"
        onClick={() => setOpen(true)}
        sx={{ cursor: "pointer" }}
      >
        {images.length > 1 ? (
          images.slice(0, 2).map((src, i) => {
            const url = getAmazonUrl(i);
            return (
              <Box
                key={src}
                component={url ? "a" : "div"}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e: React.MouseEvent) => url && e.stopPropagation()}
                sx={{
                  display: "flex",
                  width: 48,
                  height: 48,
                  overflow: "hidden",
                  borderRadius: 1,
                  border: "1px solid #ddd",
                  "&:hover img": { transform: "scale(1.1)" },
                }}
              >
                <SafeImage
                  src={src}
                  alt={`img-${i}`}
                  width={48}
                  height={48}
                  style={{
                    objectFit: "cover",
                    transition: "transform 0.3s ease",
                  }}
                />
              </Box>
            );
          })
        ) : (
          <Box
            component={getAmazonUrl(0) ? "a" : "div"}
            href={getAmazonUrl(0)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e: React.MouseEvent) =>
              getAmazonUrl(0) && e.stopPropagation()
            }
            sx={{
              display: "contents",
              width: "100%",
              height: "100%",
            }}
          >
            <SafeImage
              src={images[0]}
              alt={`img-0`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transition: "transform 0.3s ease",
              }}
            />
          </Box>
        )}
        {images && images.length > 2 && (
          <Typography variant="caption" color="textSecondary">
            +{images.length - 2}
          </Typography>
        )}
      </Box>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 2,
            borderRadius: 2,
            maxWidth: images.length === 1 ? 400 : 800,
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <Typography variant="h6" mb={2}>
            {translateWithoutPrefix("common.images")}
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns={
              images.length === 1
                ? "1fr"
                : "repeat(auto-fill, minmax(200px, 1fr))"
            }
            gap={2}
          >
            {images.map((src, i) => {
              const url = getAmazonUrl(i);
              return (
                <Box
                  key={src}
                  component={url ? "a" : "div"}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    width: "100%",
                    height: images.length === 1 ? "auto" : 180,
                    overflow: "hidden",
                    borderRadius: 1,
                    border: "1px solid #ddd",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: url ? "pointer" : "default",
                    "&:hover": url ? { opacity: 0.85 } : {},
                  }}
                >
                  <SafeImage
                    src={src}
                    height={images.length === 1 ? 300 : 180}
                    alt={`modal-img-${i}`}
                    style={{
                      width: "100%",
                      maxHeight: "70vh",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      </Modal>
    </>
  );
};
